// @givmo/org-console — Profile → Branding (Email Campaign plan EMC-WEB11; D-EMC-20 (c); mockups 12a and
// 12b): how the organization looks and sounds in its emails, posts and pages. The email renderer and
// the assistant read it (`GET /org/{ref}/profile/branding`); this tab is where a person changes it.
//
// ══ ONE SAVE LINE FOR THE FORM, AN UPLOAD FOR EACH LOGO ═════════════════════════════════════════
//
// The colours, fonts and words are ONE replace on the server, so they save together from the save
// line at the top ("All changes saved" / Save, as drawn). A logo uploads the moment it is chosen (its
// own operation) and is never part of the draft: choosing one never discards an unsaved edit, and the
// save line never claims a logo is unsaved.
//
// ══ THE QR DESIGN IS THE HOST'S EDITOR, HOUSED HERE ═════════════════════════════════════════════
//
// D-EMC-20 (c) puts the organization's saved QR design on this tab. The host hands in the ONE shared
// editor (`OrgQrDesignEditor` on the public code) with its `saveLine`, so it saves from the same save
// line at the top right as the rest of the tab (`OrgSaveLine`), while authority, previews and the PUT
// stay the editor's own (§10). With it, the tab splits in two behind a sub-navigation ("Logos, colors
// and words" · "QR code design"), both kept mounted so neither loses an edit. Absent ⇒ no split.
//
// Availability is the read's own answer: a 404 is the calm not-available state (§4), never a flag.

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorMessage,
  Field,
  Icon,
  Select,
  Skeleton,
  Textarea,
  cn,
  testIdProp,
} from '@givmo/design-system';
import { ImageIcon, Palette, Plus } from 'lucide-react';
import {
  BRAND_COLOR_ROLES,
  BRAND_LOGO_SLOTS,
  brandRefusalMessage,
  brandUploadRefusal,
  fetchOrgBrand,
  orgBrandKey,
  replaceOrgBrand,
  uploadOrgBrandLogo,
  versionedUrl,
  type BrandColor,
  type BrandColorRole,
  type BrandLogoSlot,
  type OrgBrand,
} from './org-brand-wire';
import {
  BRAND_ROLE_LABELS,
  brandDraftChanged,
  brandDraftFrom,
  brandPutFrom,
  colorUsageLine,
  fontFromOptionValue,
  fontOptionValue,
  fontOptions,
  fontSampleFamily,
  normalizeHex,
  withColorRole,
  type BrandDraft,
  type BrandFontDraft,
} from './org-brand-model';
import { isOrgConsoleUnavailable } from './org-console-transport';
import { OrgSaveLine } from './org-save-line';
import { PROFILE_COPY } from './profile-copy';

const COPY = PROFILE_COPY.branding;

export interface OrgBrandingPanelProps {
  orgRef: string;
  /** The organization's name (the host's QR editor carries its own words for the QR view). */
  orgName: string;
  /** Whether this person may change the brand (the Profile page's own write, as the server maps it). */
  canEdit: boolean;
  /** The host's organization QR design editor, housed on this tab; absent ⇒ no QR section. */
  qrDesignSlot?: ReactNode;
  /** Whether the housed QR design holds an edit not yet saved (the host hears it from the editor). */
  qrDesignUnsaved?: boolean;
  /**
   * Told whenever the form starts or stops holding an edit not yet saved, so the host's
   * unsaved-changes guard asks before a navigation, an organization switch or closing the tab
   * discards it. Pass a stable function.
   */
  onUnsavedChange?: (unsaved: boolean) => void;
  testIdPrefix: string;
}

export function OrgBrandingPanel({
  orgRef,
  orgName,
  canEdit,
  qrDesignSlot,
  qrDesignUnsaved = false,
  onUnsavedChange,
  testIdPrefix,
}: OrgBrandingPanelProps) {
  const prefix = `${testIdPrefix}-branding`;
  const brand = useQuery({
    queryKey: orgBrandKey(orgRef),
    queryFn: ({ signal }) => fetchOrgBrand(orgRef, signal),
    retry: false,
    enabled: orgRef.length > 0,
  });

  if (brand.isPending) {
    return (
      <Card className="p-24" testId={`${prefix}-loading`}>
        <div className="flex flex-col gap-10">
          <Skeleton className="h-16 w-1/3" />
          <Skeleton className="h-12 w-2/3" />
        </div>
      </Card>
    );
  }
  if (brand.isError) {
    return isOrgConsoleUnavailable(brand.error) ? (
      <EmptyState
        testId={`${prefix}-unavailable`}
        icon={<Icon icon={Palette} size={20} aria-hidden />}
        title={COPY.unavailable.title}
        description={COPY.unavailable.body}
      />
    ) : (
      <Card testId={`${prefix}-error`}>
        <CardBody className="flex flex-col items-start gap-12">
          <p className="font-sans text-s14 text-text-secondary">{COPY.loadError}</p>
          <Button
            appearance="saas"
            tone="secondary"
            size="sm"
            onClick={() => void brand.refetch()}
            testId={`${prefix}-retry`}
          >
            {PROFILE_COPY.common.retry}
          </Button>
        </CardBody>
      </Card>
    );
  }
  return (
    <BrandingForm
      key={orgRef}
      orgRef={orgRef}
      orgName={orgName}
      brand={brand.data}
      canEdit={canEdit}
      qrDesignSlot={qrDesignSlot}
      qrDesignUnsaved={qrDesignUnsaved}
      onUnsavedChange={onUnsavedChange}
      prefix={prefix}
    />
  );
}

function BrandingForm({
  orgRef,
  orgName,
  brand,
  canEdit,
  qrDesignSlot,
  qrDesignUnsaved,
  onUnsavedChange,
  prefix,
}: {
  orgRef: string;
  orgName: string;
  brand: OrgBrand;
  canEdit: boolean;
  qrDesignSlot?: ReactNode;
  qrDesignUnsaved: boolean;
  onUnsavedChange?: (unsaved: boolean) => void;
  prefix: string;
}) {
  const queryClient = useQueryClient();
  // What the server holds, and what the form holds. A logo upload refreshes `brand` (the query) and
  // neither of these, so an unsaved edit survives it.
  const [baseline, setBaseline] = useState<BrandDraft>(() => brandDraftFrom(brand));
  const [draft, setDraft] = useState<BrandDraft>(baseline);
  const changed = brandDraftChanged(draft, baseline);

  const save = useMutation({
    mutationFn: (submitted: BrandDraft) => replaceOrgBrand(orgRef, brandPutFrom(submitted)),
    onSuccess: (next, submitted) => {
      queryClient.setQueryData(orgBrandKey(orgRef), next);
      const saved = brandDraftFrom(next);
      setBaseline(saved);
      // The form takes the server's answer only if nothing was typed while the save was out; an edit
      // made meanwhile stays in the form, still unsaved, so the save never erases it.
      setDraft((current) => (brandDraftChanged(current, submitted) ? current : saved));
    },
  });

  useEffect(() => {
    onUnsavedChange?.(changed && !save.isPending);
  }, [changed, save.isPending, onUnsavedChange]);
  useEffect(() => () => onUnsavedChange?.(false), [onUnsavedChange]);

  const update = (patch: Partial<BrandDraft>) => {
    // A failed save's sentence clears on the next edit; a save still out keeps its state.
    if (save.isError) save.reset();
    setDraft((current) => ({ ...current, ...patch }));
  };

  const [section, setSection] = useState<BrandingSection>('brand');
  const housesQr = qrDesignSlot !== undefined && qrDesignSlot !== null;

  const brandView = (
    <div className="flex flex-col gap-16">
      <div className="flex flex-wrap items-center justify-between gap-12">
        <p className="font-sans text-s14 text-text-secondary">{COPY.lede}</p>
        {canEdit ? (
          <OrgSaveLine
            state={save.isPending ? 'saving' : changed ? 'unsaved' : 'saved'}
            canSave={changed}
            onSave={() => save.mutate(draft)}
            testIdPrefix={prefix}
          />
        ) : (
          <p
            className="font-sans text-s13 text-text-tertiary"
            {...testIdProp(`${prefix}-read-only`)}
          >
            {COPY.readOnly}
          </p>
        )}
      </div>
      {save.isError && (
        <ErrorMessage testId={`${prefix}-save-error`}>
          {brandRefusalMessage(save.error, COPY.saveFailed, COPY.fieldRefused)}
        </ErrorMessage>
      )}

      <LogosCard orgRef={orgRef} brand={brand} canEdit={canEdit} prefix={prefix} />

      <div className="grid grid-cols-1 items-stretch gap-16 lg:grid-cols-2">
        <ColorsCard
          colors={draft.colors}
          canEdit={canEdit}
          onChange={(colors) => update({ colors })}
          prefix={prefix}
        />
        <FontsCard
          brand={brand}
          draft={draft}
          canEdit={canEdit}
          onChange={update}
          prefix={prefix}
        />
      </div>

      <Card flush testId={`${prefix}-words`}>
        <CardHeader title={COPY.words.title} subtitle={COPY.words.subtitle} />
        <CardBody className="flex flex-col gap-14">
          <Field
            label={COPY.words.tagline}
            value={draft.tagline}
            maxLength={200}
            disabled={!canEdit}
            onChange={(event) => update({ tagline: event.target.value })}
            testId={`${prefix}-tagline`}
          />
          <Textarea
            label={COPY.words.voice}
            value={draft.voiceNotes}
            maxLength={4000}
            rows={3}
            placeholder={COPY.words.voicePlaceholder}
            disabled={!canEdit}
            onChange={(event) => update({ voiceNotes: event.target.value })}
            testId={`${prefix}-voice`}
          />
        </CardBody>
      </Card>
    </div>
  );

  if (!housesQr) {
    return (
      <div className="flex flex-col gap-16" {...testIdProp(prefix)}>
        {brandView}
      </div>
    );
  }
  // Both views stay mounted: switching between them never loses a draft or a working design.
  return (
    <div className="flex flex-col gap-16" {...testIdProp(prefix)}>
      <BrandingSections
        value={section}
        onChange={setSection}
        unsaved={{ brand: changed, qr: qrDesignUnsaved }}
        orgName={orgName}
        prefix={prefix}
      />
      <div
        id={`${prefix}-section-brand`}
        role="tabpanel"
        aria-labelledby={`${prefix}-section-brand-tab`}
        hidden={section !== 'brand'}
      >
        {brandView}
      </div>
      <div
        id={`${prefix}-section-qr`}
        role="tabpanel"
        aria-labelledby={`${prefix}-section-qr-tab`}
        hidden={section !== 'qr'}
        {...testIdProp(`${prefix}-qr`)}
      >
        {qrDesignSlot}
      </div>
    </div>
  );
}

type BrandingSection = 'brand' | 'qr';

/**
 * Branding's two parts, as a segmented control under the page's tabs: a person always sees which
 * part they are in and the one-click way back. A part holding an edit not yet saved wears the save
 * line's dot, so it shows from the other part too (review-polish B-2).
 */
function BrandingSections({
  value,
  onChange,
  unsaved,
  orgName,
  prefix,
}: {
  value: BrandingSection;
  onChange: (next: BrandingSection) => void;
  unsaved: Record<BrandingSection, boolean>;
  orgName: string;
  prefix: string;
}) {
  const items: { key: BrandingSection; label: string }[] = [
    { key: 'brand', label: COPY.sections.brand },
    { key: 'qr', label: COPY.sections.qr },
  ];
  return (
    <div
      role="tablist"
      aria-label={`${COPY.sections.label}, ${orgName}`}
      className="flex w-fit max-w-full flex-wrap gap-2 rounded-lg border border-border-subtle bg-surface p-2"
      {...testIdProp(`${prefix}-sections`)}
    >
      {items.map(({ key, label }) => {
        const selected = value === key;
        return (
          <button
            key={key}
            type="button"
            role="tab"
            id={`${prefix}-section-${key}-tab`}
            aria-selected={selected}
            aria-controls={`${prefix}-section-${key}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(key)}
            onKeyDown={(event) => {
              if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
              event.preventDefault();
              const next = items[(items.findIndex((i) => i.key === key) + 1) % items.length]!;
              onChange(next.key);
              document.getElementById(`${prefix}-section-${next.key}-tab`)?.focus();
            }}
            className={cn(
              'rounded-md px-14 py-6 font-sans text-s14 transition-colors focus-visible:shadow-ring focus-visible:outline-none max-lg:min-h-44',
              selected
                ? 'bg-brand-50 font-w600 text-brand-700'
                : 'font-w500 text-text-secondary hover:bg-surface-hover hover:text-text-strong',
            )}
            {...testIdProp(`${prefix}-section-${key}`)}
          >
            <span className="inline-flex items-center gap-6">
              {label}
              {unsaved[key] && (
                <>
                  <span
                    aria-hidden
                    className="size-6 shrink-0 rounded-full bg-warning-500"
                    {...testIdProp(`${prefix}-section-${key}-unsaved`)}
                  />
                  <span className="sr-only">{`, ${COPY.unsaved}`}</span>
                </>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ── Logos ────────────────────────────────────────────────────────────────────────────────────────

const LOGO_LABELS: Record<BrandLogoSlot, string> = {
  primary: COPY.logos.primary,
  alternate: COPY.logos.alternate,
  dark: COPY.logos.dark,
};

function LogosCard({
  orgRef,
  brand,
  canEdit,
  prefix,
}: {
  orgRef: string;
  brand: OrgBrand;
  canEdit: boolean;
  prefix: string;
}) {
  const queryClient = useQueryClient();
  const [failure, setFailure] = useState<string | null>(null);
  const upload = useMutation({
    mutationFn: ({ slot, file }: { slot: BrandLogoSlot; file: File }) =>
      uploadOrgBrandLogo(orgRef, slot, file),
    onMutate: () => setFailure(null),
    onSuccess: (next) => queryClient.setQueryData(orgBrandKey(orgRef), next),
    onError: (error) => setFailure(brandRefusalMessage(error, COPY.logos.failed)),
  });
  // A Replace overwrites the file at the same address; the brand's own change time busts the cache.

  return (
    <Card flush testId={`${prefix}-logos`}>
      <CardHeader title={COPY.logos.title} />
      <CardBody className="flex flex-col gap-12">
        <div className="grid grid-cols-1 gap-24 sm:grid-cols-3">
          {BRAND_LOGO_SLOTS.map((slot) => (
            <LogoSlotTile
              key={slot}
              slot={slot}
              src={brand.logos[slot] ? versionedUrl(brand.logos[slot].url, brand.updatedAt) : null}
              canEdit={canEdit}
              uploading={upload.isPending && upload.variables?.slot === slot}
              disabled={upload.isPending}
              onFile={(file) => {
                const refusal = brandUploadRefusal(file, 'logo');
                if (refusal !== null) setFailure(refusal);
                else upload.mutate({ slot, file });
              }}
              prefix={prefix}
            />
          ))}
        </div>
        {failure !== null && <ErrorMessage testId={`${prefix}-logo-error`}>{failure}</ErrorMessage>}
      </CardBody>
    </Card>
  );
}

function LogoSlotTile({
  slot,
  src,
  canEdit,
  uploading,
  disabled,
  onFile,
  prefix,
}: {
  slot: BrandLogoSlot;
  src: string | null;
  canEdit: boolean;
  uploading: boolean;
  disabled: boolean;
  onFile: (file: File) => void;
  prefix: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  const dark = slot === 'dark';
  return (
    <div className="flex min-w-0 flex-col gap-8" {...testIdProp(`${prefix}-logo-${slot}`)}>
      <div
        className={cn(
          'flex h-[92px] items-center justify-center overflow-hidden rounded-lg border px-16',
          // The dark logo on the navy mockup 12a draws it on.
          dark ? 'border-transparent bg-[#12355B]' : 'border-border-subtle bg-surface-sunken',
        )}
      >
        {src !== null ? (
          <img
            src={src}
            alt={LOGO_LABELS[slot]}
            className="max-h-[72px] max-w-full object-contain"
          />
        ) : (
          <span
            className={cn(
              'flex flex-col items-center gap-4 font-sans text-s12',
              dark ? 'text-neutral-300' : 'text-text-tertiary',
            )}
          >
            <Icon icon={ImageIcon} size={20} aria-hidden />
            {canEdit ? COPY.logos.empty : COPY.logos.none}
          </span>
        )}
      </div>
      <div className="flex items-center justify-between gap-8">
        <span className="font-sans text-s13 font-w600 text-text-strong">{LOGO_LABELS[slot]}</span>
        {canEdit && (
          <>
            <Button
              appearance="saas"
              tone="link"
              size="sm"
              disabled={disabled}
              aria-label={`${src !== null ? COPY.logos.replace : COPY.logos.upload} ${LOGO_LABELS[slot]}`}
              onClick={() => input.current?.click()}
              testId={`${prefix}-logo-${slot}-replace`}
            >
              {uploading
                ? COPY.logos.uploading
                : src !== null
                  ? COPY.logos.replace
                  : COPY.logos.upload}
            </Button>
            <input
              ref={input}
              type="file"
              accept="image/png,image/jpeg"
              className="hidden"
              onChange={(event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (file) onFile(file);
              }}
              {...testIdProp(`${prefix}-logo-${slot}-input`)}
            />
          </>
        )}
      </div>
    </div>
  );
}

// ── Colours ──────────────────────────────────────────────────────────────────────────────────────

/** The colour dialog's subject: a colour being edited (its index), or a new one. */
type ColorEditing = { index: number } | { index: null };

function ColorsCard({
  colors,
  canEdit,
  onChange,
  prefix,
}: {
  colors: BrandColor[];
  canEdit: boolean;
  onChange: (colors: BrandColor[]) => void;
  prefix: string;
}) {
  const [editing, setEditing] = useState<ColorEditing | null>(null);
  return (
    <Card flush testId={`${prefix}-colors`}>
      <CardHeader
        title={COPY.colors.title}
        actions={
          canEdit && colors.length < 12 ? (
            <Button
              appearance="saas"
              tone="ghost"
              size="sm"
              leadingIcon={<Icon icon={Plus} size={14} aria-hidden />}
              onClick={() => setEditing({ index: null })}
              testId={`${prefix}-color-add`}
            >
              {COPY.colors.add}
            </Button>
          ) : undefined
        }
      />
      <CardBody>
        {colors.length === 0 ? (
          <p className="font-sans text-s14 text-text-secondary">{COPY.colors.empty}</p>
        ) : (
          <ul className="grid grid-cols-1 gap-x-16 gap-y-16 sm:grid-cols-2">
            {colors.map((color, index) => {
              const body = (
                <>
                  <span
                    className="size-[40px] shrink-0 rounded-lg ring-1 ring-black/10"
                    style={{ background: color.hex }}
                    aria-hidden
                  />
                  <span className="flex min-w-0 flex-col text-left">
                    <span className="truncate font-sans text-s13 font-w600 text-text-strong">
                      {color.name}{' '}
                      <span className="font-mono text-s12 font-w400 text-text-tertiary">
                        {color.hex.toUpperCase()}
                      </span>
                    </span>
                    <span className="truncate font-sans text-s12 text-text-secondary">
                      {colorUsageLine(color.roles, BRAND_COLOR_ROLES)}
                    </span>
                  </span>
                </>
              );
              return (
                <li key={`${color.hex}-${index}`} className="min-w-0">
                  {canEdit ? (
                    <button
                      type="button"
                      className="-m-4 flex w-[calc(100%+8px)] items-center gap-10 rounded-lg p-4 hover:bg-surface-hover focus-visible:shadow-ring focus-visible:outline-none"
                      aria-label={COPY.colors.edit(color.name)}
                      onClick={() => setEditing({ index })}
                      {...testIdProp(`${prefix}-color-${index}`)}
                    >
                      {body}
                    </button>
                  ) : (
                    <span
                      className="flex items-center gap-10"
                      {...testIdProp(`${prefix}-color-${index}`)}
                    >
                      {body}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </CardBody>
      {editing !== null && (
        <ColorDialog
          colors={colors}
          index={editing.index}
          onClose={() => setEditing(null)}
          onApply={(next) => {
            onChange(next);
            setEditing(null);
          }}
          prefix={prefix}
        />
      )}
    </Card>
  );
}

function ColorDialog({
  colors,
  index,
  onClose,
  onApply,
  prefix,
}: {
  colors: BrandColor[];
  index: number | null;
  onClose: () => void;
  onApply: (colors: BrandColor[]) => void;
  prefix: string;
}) {
  const existing = index !== null ? colors[index] : undefined;
  const [name, setName] = useState(existing?.name ?? '');
  const [hexText, setHexText] = useState((existing?.hex ?? '#327dc0').toUpperCase());
  const [roles, setRoles] = useState<BrandColorRole[]>(existing?.roles ?? []);
  const [tried, setTried] = useState(false);
  const hex = normalizeHex(hexText);
  const nameMissing = name.trim().length === 0;

  const holderOf = (role: BrandColorRole) =>
    colors.find((color, i) => i !== index && color.roles.includes(role))?.name ?? null;
  // The colours this change would leave with no use at all: each says so before it is applied.
  const leftUnused = colors
    .filter(
      (color, i) =>
        i !== index &&
        color.roles.length > 0 &&
        color.otherRoles.length === 0 &&
        color.roles.every((role) => roles.includes(role)),
    )
    .map((color) => color.name);

  const apply = () => {
    setTried(true);
    if (nameMissing || hex === null) return;
    // The colour goes in (or replaces itself), then each role it takes moves off any other colour.
    const self: BrandColor = {
      name: name.trim(),
      hex,
      roles: [],
      otherRoles: existing?.otherRoles ?? [],
    };
    let next = index !== null ? colors.map((c, i) => (i === index ? self : c)) : [...colors, self];
    const at = index ?? next.length - 1;
    for (const role of roles) next = withColorRole(next, at, role, true);
    onApply(next);
  };

  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Content
        aria-label={index !== null ? COPY.colors.dialogEdit : COPY.colors.dialogAdd}
        width={440}
        className="flex flex-col gap-16"
        testId={`${prefix}-color-dialog`}
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          {index !== null ? COPY.colors.dialogEdit : COPY.colors.dialogAdd}
        </Dialog.Title>
        <Field
          label={COPY.colors.name}
          value={name}
          maxLength={40}
          onChange={(event) => setName(event.target.value)}
          error={tried && nameMissing}
          errorMessage={tried && nameMissing ? COPY.colors.nameRequired : undefined}
          testId={`${prefix}-color-name`}
        />
        <div className="flex flex-col gap-6">
          <span className="font-sans text-s12 font-w600 text-text-strong">{COPY.colors.value}</span>
          <div className="flex items-center gap-12">
            <input
              type="color"
              aria-label={COPY.colors.value}
              value={hex ?? '#000000'}
              onChange={(event) => setHexText(event.target.value.toUpperCase())}
              className="size-36 shrink-0 cursor-pointer rounded-md border border-border-strong bg-surface p-2 [&::-webkit-color-swatch]:rounded-[4px] [&::-webkit-color-swatch]:border-0 [&::-webkit-color-swatch-wrapper]:p-0"
              {...testIdProp(`${prefix}-color-swatch`)}
            />
            <Field
              aria-label={`${COPY.colors.value} hex value`}
              value={hexText}
              maxLength={7}
              spellCheck={false}
              autoComplete="off"
              containerClassName="w-[140px]"
              className="font-mono"
              onChange={(event) => setHexText(event.target.value.toUpperCase())}
              error={tried && hex === null}
              testId={`${prefix}-color-hex`}
            />
          </div>
          {tried && hex === null && <ErrorMessage>{COPY.colors.hexInvalid}</ErrorMessage>}
        </div>
        <fieldset className="flex flex-col gap-8">
          <legend className="mb-4 font-sans text-s12 font-w600 text-text-strong">
            {COPY.colors.roles}
          </legend>
          {BRAND_COLOR_ROLES.map((role) => {
            const on = roles.includes(role);
            const holder = holderOf(role);
            return (
              <label key={role} className="flex cursor-pointer items-center gap-10">
                <Checkbox
                  appearance="saas"
                  checked={on}
                  onCheckedChange={(checked) =>
                    setRoles((current) =>
                      checked
                        ? [...current.filter((r) => r !== role), role]
                        : current.filter((r) => r !== role),
                    )
                  }
                  testId={`${prefix}-color-role-${role}`}
                />
                <span className="font-sans text-s14 text-text">{BRAND_ROLE_LABELS[role]}</span>
                {on && holder !== null && (
                  <span className="font-sans text-s12 text-text-tertiary">
                    {COPY.colors.movesFrom(holder)}
                  </span>
                )}
              </label>
            );
          })}
          <span className="font-sans text-s12 text-text-tertiary">{COPY.colors.rolesHelper}</span>
          {leftUnused.map((unused) => (
            <span
              key={unused}
              className="font-sans text-s12 font-w600 text-warning-700"
              {...testIdProp(`${prefix}-color-left-unused`)}
            >
              {COPY.colors.leftUnused(unused)}
            </span>
          ))}
        </fieldset>
        <p className="font-sans text-s12 text-text-tertiary">{COPY.colors.applyNote}</p>
        <div className="mt-8 flex items-center gap-8">
          {index !== null && (
            <Button
              appearance="saas"
              tone="ghost"
              size="md"
              className="text-danger-600"
              onClick={() => onApply(colors.filter((_, i) => i !== index))}
              testId={`${prefix}-color-remove`}
            >
              {COPY.colors.remove}
            </Button>
          )}
          <span className="ml-auto flex items-center gap-8">
            <Dialog.Close render={<Button appearance="saas" tone="secondary" size="md" />}>
              {COPY.colors.cancel}
            </Dialog.Close>
            <Button
              appearance="saas"
              tone="primary"
              size="md"
              onClick={apply}
              testId={`${prefix}-color-done`}
            >
              {COPY.colors.apply}
            </Button>
          </span>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}

// ── Fonts ────────────────────────────────────────────────────────────────────────────────────────

function FontsCard({
  brand,
  draft,
  canEdit,
  onChange,
  prefix,
}: {
  brand: OrgBrand;
  draft: BrandDraft;
  canEdit: boolean;
  onChange: (patch: Partial<BrandDraft>) => void;
  prefix: string;
}) {
  const options = fontOptions(brand.fontChoices);
  const fallbackLabel =
    brand.fontChoices.find((font) => font.key === draft.fallback)?.label ?? 'Arial';
  // Fonts are a pair on the server: choosing one while neither is set fills the other with the
  // fallback, visibly, so a half pair is never saved.
  const choose = (role: 'headings' | 'body', value: string | null) => {
    const font = value !== null ? fontFromOptionValue(value) : null;
    if (font === null) return;
    const standIn: BrandFontDraft = { family: draft.fallback, weight: 'regular' };
    onChange(
      role === 'headings'
        ? { headings: font, body: draft.body ?? standIn }
        : { body: font, headings: draft.headings ?? { ...standIn, weight: 'bold' } },
    );
  };
  const rows: { role: 'headings' | 'body'; label: string; sample: string }[] = [
    { role: 'headings', label: COPY.fonts.headings, sample: COPY.fonts.headingsSample },
    { role: 'body', label: COPY.fonts.body, sample: COPY.fonts.bodySample },
  ];
  return (
    <Card flush testId={`${prefix}-fonts`}>
      <CardHeader title={COPY.fonts.title} subtitle={COPY.fonts.subtitle(fallbackLabel)} />
      <CardBody className="grid grid-cols-1 gap-16 sm:grid-cols-2">
        {rows.map(({ role, label, sample }) => {
          const font = draft[role];
          return (
            <div key={role} className="flex min-w-0 flex-col gap-8">
              <Select
                appearance="saas"
                label={label}
                placeholder={COPY.fonts.choose}
                value={font !== null ? fontOptionValue(font) : null}
                options={options}
                disabled={!canEdit}
                onValueChange={(value) => choose(role, value)}
                testId={`${prefix}-font-${role}`}
              />
              <span
                className="truncate text-[17px] text-text-strong"
                style={{
                  fontFamily: fontSampleFamily(font?.family ?? draft.fallback, brand.fontChoices),
                  fontWeight: font?.weight === 'bold' ? 700 : 400,
                }}
                {...testIdProp(`${prefix}-font-${role}-sample`)}
              >
                {sample}
              </span>
            </div>
          );
        })}
      </CardBody>
    </Card>
  );
}
